import {
  Alert,
  Box,
  Button,
  Card,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LiveTvOutlinedIcon from "@mui/icons-material/LiveTvOutlined";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { tokens } from "../theme";
import { loginErrorMessage } from "../auth/loginErrors";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // "/" so the home redirect decides where this user belongs (Fleet health
  // for anyone who may see the fleet, Premises otherwise).
  const next = searchParams.get("next") ?? "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Already authenticated? Bounce immediately.
  useEffect(() => {
    if (!loading && user) navigate(next, { replace: true });
  }, [loading, user, navigate, next]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await login(email, password);
      navigate(next, { replace: true });
    } catch (e) {
      // Name the actual failure. "Invalid email or password" on a server that
      // is simply down is the single most misleading message this app can
      // show: it is confidently wrong, and it points at the one thing the
      // reader cannot fix by waiting. A rate limit reported as "offline" is
      // the same mistake pointing the other way — it hides the one thing the
      // reader CAN fix by waiting. `loginErrors` holds all three cases.
      const failed = e as Error & { retryAfter?: number | null };
      setErr(
        loginErrorMessage({
          kind:
            failed?.message === "rate_limited" ||
            failed?.message === "server_unreachable"
              ? failed.message
              : "invalid_credentials",
          retryAfter: failed?.retryAfter ?? null,
        })
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        bgcolor: "background.default",
        px: 2,
        // Soft brand glow behind the card.
        backgroundImage: `radial-gradient(60rem 60rem at 50% -20%, ${tokens.brandTint}, transparent 60%)`,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 400,
          p: { xs: 3, sm: 4.5 },
          boxShadow:
            "0 12px 40px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.06)",
        }}
        data-testid="login-paper"
      >
        <Stack spacing={3}>
          <Stack spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                color: "#fff",
                background: `linear-gradient(135deg, ${tokens.brand}, ${tokens.brandStrong})`,
              }}
            >
              <LiveTvOutlinedIcon />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Montserrat, Inter, sans-serif",
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                }}
              >
                Foyer
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Sign in to manage your hotel TV fleet.
              </Typography>
            </Box>
          </Stack>

          <form onSubmit={submit}>
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                size="medium"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                autoComplete="email"
                required
                fullWidth
                inputProps={{ "data-testid": "login-email" }}
              />
              <TextField
                label="Password"
                type="password"
                size="medium"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                fullWidth
                inputProps={{ "data-testid": "login-password" }}
              />
              {err && (
                <Alert severity="error" data-testid="login-error">
                  {err}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                size="large"
                disableElevation
                disabled={busy || !email || !password}
                data-testid="login-submit"
                sx={{ py: 1.25 }}
              >
                {busy ? "Signing in…" : "Sign in"}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Card>
    </Box>
  );
}
